import { LocationMapper, OptimizerConfig, TrafficOptions, TruckOptions } from '@nx-smartmonkey/shared/domain';

import { ProjectBase } from '../../domain/backoffice/project/ProjectBase';
import { ProjectBaseDTO } from '../../domain/backoffice/project/ProjectBaseDTO';
import { ProjectBaseRepositoryDTO } from '../../domain/backoffice/project/ProjectBaseRepositoryDTO';
import { ProjectUnits } from '../../domain/project/ProjectUnits';
import { GeoFenceMapper } from '../geoFence/GeoFenceMapper';
import { ProjectCustomFieldsMapper } from '../project/ProjectCustomFieldsMapper';
import { ProjectCustomerEmailMapper } from '../project/ProjectCustomerEmailMapper';
import { ProjectCustomerSMSMapper } from '../project/ProjectCustomerSMSMapper';
import { ProjectCustomerWhatsAppMapper } from '../project/ProjectCustomerWhatsAppMapper';
import { ProjectViewMapper } from '../project/ProjectViewMapper';
import { ProjectResourcesMapper } from './ProjectResourcesMapper';

class ProjectBaseMapper {
  static toDomain(props: ProjectBaseDTO): ProjectBase {
    const {
      id,
      organization_id,
      label,
      public_key,
      location,
      description,
      avatar_url,
      view,
      optimizer_config,
      traffic_options,
      units,
      custom_fields,
      customer_email,
      customer_sms,
      customer_whatsapp,
      tasks_enabled,
      geo_fences_enabled,
      geo_fences,
      managed,
      resources,
      deleted,
      created_by,
      created_at,
      updated_at,
      deleted_at,
    } = props;

    const locationDomain = location ? LocationMapper.toDomain(location) : undefined;
    const viewDomain = ProjectViewMapper.toDomain(view);
    const optimizerConfigDomain = optimizer_config
      ? OptimizerConfig.create({
          max_wait_time: optimizer_config.max_wait_time,
          first_solution_strategy: optimizer_config.first_solution_strategy,
          local_search_strategy: optimizer_config.local_search_strategy,
          time_limit_seconds: optimizer_config.time_limit_seconds,
          lns_time_limit: optimizer_config.lns_time_limit,
          balance_services: optimizer_config.balance_services,
          service_duration: optimizer_config.service_duration,
          operation_country: optimizer_config.operation_country,
          time_span_coefficient: optimizer_config.time_span_coefficient,
          distance_span_coefficient: optimizer_config.distance_span_coefficient,
          use_distance: optimizer_config.use_distance,
          drop_penalty: optimizer_config.drop_penalty,
          distance_balance: optimizer_config.distance_balance,
          isolation_factor: optimizer_config.isolation_factor,
        })
      : undefined;
    const trafficOptionsDomain = traffic_options
      ? TrafficOptions.create({
          multiplier: traffic_options.multiplier,
          mode: traffic_options.mode,
          truck: traffic_options.truck
            ? TruckOptions.create({
                shipped_hazardous_goods: traffic_options.truck.shipped_hazardous_goods,
                gross_weight: traffic_options.truck.gross_weight,
                height: traffic_options.truck.height,
                width: traffic_options.truck.width,
                length: traffic_options.truck.length,
                tunnel_category: traffic_options.truck.tunnel_category,
                axle_count: traffic_options.truck.axle_count,
                type: traffic_options.truck.type,
                trailer_count: traffic_options.truck.trailer_count,
              })
            : undefined,
          avoid: traffic_options.avoid,
        })
      : undefined;

    const projectUnitsDomain = units ? ProjectUnits.create(units) : undefined;
    const projectCustomFieldsDomain = custom_fields ? ProjectCustomFieldsMapper.toDomain(custom_fields) : undefined;
    const projectCustomerEmailDomain = ProjectCustomerEmailMapper.toDomain(customer_email);
    const projectCustomerSMSDomain = ProjectCustomerSMSMapper.toDomain(customer_sms);
    const projectCustomerWhatsAppDomain = ProjectCustomerWhatsAppMapper.toDomain(customer_whatsapp);
    const geoFencesDomain = geo_fences ? geo_fences.map(GeoFenceMapper.toDomain) : [];

    const projectResources = ProjectResourcesMapper.toDomain(resources);

    return ProjectBase.create(
      {
        organization_id,
        label,
        public_key,
        location: locationDomain,
        description,
        avatar_url,
        view: viewDomain,
        optimizer_config: optimizerConfigDomain,
        traffic_options: trafficOptionsDomain,
        units: projectUnitsDomain,
        custom_fields: projectCustomFieldsDomain,
        customer_email: projectCustomerEmailDomain,
        customer_sms: projectCustomerSMSDomain,
        customer_whatsapp: projectCustomerWhatsAppDomain,
        tasks_enabled,
        geo_fences_enabled,
        geo_fences: geoFencesDomain,
        managed,
        resources: projectResources,
        deleted,
        created_by,
        created_at,
        updated_at,
        deleted_at,
      },
      id
    );
  }

  static toRepositoryAsMonkey(project: ProjectBase): ProjectBaseRepositoryDTO {
    return {
      label: project.label,
      description: project.description,
      location: project.location ? project.location.getValue() : undefined,
      view: ProjectViewMapper.toRepository(project.view),
      ...(project.units ? { units: project.units.getValue() } : {}),
      ...(project.optimizer_config
        ? {
            optimizer_config: {
              balance_services: project.optimizer_config?.balance_services,
              first_solution_strategy: project.optimizer_config?.first_solution_strategy,
              lns_time_limit: project.optimizer_config?.lns_time_limit,
              local_search_strategy: project.optimizer_config?.local_search_strategy,
              max_wait_time: project.optimizer_config?.max_wait_time,
              operation_country: project.optimizer_config?.operation_country,
              service_duration: project.optimizer_config?.service_duration,
              time_limit_seconds: project.optimizer_config?.time_limit_seconds,
              use_distance: project.optimizer_config?.use_distance,
              distance_span_coefficient: project.optimizer_config?.distance_span_coefficient,
              drop_penalty: project.optimizer_config?.drop_penalty,
              distance_balance: project.optimizer_config?.distance_balance,
              isolation_factor: project.optimizer_config?.isolation_factor,
              time_span_coefficient: project.optimizer_config?.time_span_coefficient,
            },
          }
        : {}),
      ...(project.traffic_options
        ? {
            traffic_options: {
              multiplier: project.traffic_options.multiplier,
              mode: project.traffic_options.mode ?? `car`,
              truck: project.traffic_options.truck?.getValue(),
            },
          }
        : {}),
    };
  }

  static toRepository(project: ProjectBase): ProjectBaseRepositoryDTO {
    const asMonkeyRepo = this.toRepositoryAsMonkey(project);
    return {
      ...asMonkeyRepo,
      tasks_enabled: project.tasks_enabled,
      traffic_options: {
        ...asMonkeyRepo.traffic_options,
        departure_at: project.traffic_options?.departure_at,
        avoid: project.traffic_options?.avoid,
      },
    };
  }
}

export default ProjectBaseMapper;
